// =====================================================================
// src/lib/seguranca.js — o módulo Segurança, a parte que não toca a tela
//
// Três abas (0057): quem está no prédio agora, as ocorrências e o
// histórico de ocupação de um apartamento. Aqui fica o que se decide sem
// navegador: agrupar por andar, contar, validar o formulário, montar as
// linhas do PDF da lista de emergência. Testado em scripts/teste_tela.mjs.
//
// O MÍNIMO DO HÓSPEDE (decisão 3A do Hugo, 23/09/2026) não é regra daqui:
// o banco já devolve só nome, apartamento, entrada e menor. Esta biblioteca
// não tem de onde tirar um documento, e isso é de propósito.
// =====================================================================

export const SITUACOES = [
  { chave: 'aberta', rotulo: 'Abertas' },
  { chave: 'encerrada', rotulo: 'Encerradas' },
  { chave: '', rotulo: 'Todas' },
];

// "8º andar"; andar nulo é o "Sem andar" da Configuração de quartos.
export const rotuloDoAndar = a => (a == null ? 'Sem andar' : (a === 0 ? 'Térreo' : `${a}º andar`));

/**
 * As linhas de `hospedados_agora` agrupadas: andar → apartamento → pessoas.
 * A ordem é a do banco (andar, apartamento, chegada), e é preservada.
 */
export function agruparPorAndar(linhas) {
  const andares = [];
  const porAndar = new Map();
  for (const l of linhas || []) {
    const chaveAndar = l.andar == null ? 'sem' : String(l.andar);
    let a = porAndar.get(chaveAndar);
    if (!a) { a = { andar: l.andar == null ? null : l.andar, rotulo: rotuloDoAndar(l.andar), apartamentos: [], _por: new Map() }; porAndar.set(chaveAndar, a); andares.push(a); }
    let ap = a._por.get(l.unidade);
    if (!ap) { ap = { unidade: l.unidade, unidade_id: l.unidade_id, pessoas: [] }; a._por.set(l.unidade, ap); a.apartamentos.push(ap); }
    ap.pessoas.push({ nome: l.nome, chegada_em: l.chegada_em, menor: !!l.menor });
  }
  return andares.map(({ _por, ...resto }) => resto);
}

// Os três números do topo: pessoas, apartamentos ocupados e menores.
export function resumoDoPredio(linhas) {
  const ls = linhas || [];
  return {
    pessoas: ls.length,
    apartamentos: new Set(ls.map(l => l.unidade_id || l.unidade)).size,
    menores: ls.filter(l => l.menor).length,
  };
}

// Filtro da lista por apartamento ou nome, sem acento e sem caixa.
const semAcento = s => String(s == null ? '' : s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
export function filtrarHospedados(linhas, texto) {
  const t = semAcento(texto).trim();
  if (!t) return linhas || [];
  return (linhas || []).filter(l => semAcento(l.unidade).includes(t) || semAcento(l.nome).includes(t));
}

// "23/09/2026 15:05" — o PDF sai de papel e vai para a mão de um bombeiro:
// ano inteiro e sem abreviação.
export function dataHora(iso) {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return '';
  const p2 = n => String(n).padStart(2, '0');
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

export const COLUNAS_EMERGENCIA = ['Andar', 'Apto', 'Nome', 'Entrada', 'Menor'];
// Uma linha por pessoa, na ordem do banco. "Menor" em palavra, não em
// ícone: impressora sem cor e leitura sob lanterna.
export function linhasDeEmergencia(linhas) {
  return (linhas || []).map(l => [rotuloDoAndar(l.andar), l.unidade, l.nome, dataHora(l.chegada_em), l.menor ? 'MENOR' : '']);
}

export const COLUNAS_HISTORICO = ['Nome', 'Entrada', 'Saída', 'Menor'];
export function linhasDeHistorico(linhas) {
  return (linhas || []).map(l => [l.nome, dataHora(l.chegada_em), l.saida_em ? dataHora(l.saida_em) : 'ainda no apartamento', l.menor ? 'MENOR' : '']);
}

// "2026-09-23T14:30" para o <input type="datetime-local">, no relógio do
// aparelho (é o que o campo mostra e devolve).
export function paraCampoDataHora(ms) {
  const d = new Date(ms);
  const p2 = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

/**
 * O formulário de ocorrência. Devolve a lista de erros (vazia = pode
 * salvar). O banco confere tudo de novo; isto só poupa a viagem.
 *   dados: { categoria, descricao, quando }   quando = valor do datetime-local
 */
export function validarOcorrencia(dados, agoraMs) {
  const erros = [];
  if (!dados || !dados.categoria) erros.push('Escolha a categoria.');
  if (!dados || !String(dados.descricao || '').trim()) erros.push('Descreva o que aconteceu.');
  if (dados && dados.quando) {
    const t = new Date(dados.quando).getTime();
    if (Number.isNaN(t)) erros.push('A data e a hora não estão certas.');
    else if (t > agoraMs + 5 * 60 * 1000) erros.push('A ocorrência não pode ser no futuro.');
  }
  return erros;
}

// O que vai para `registrar_ocorrencia`. `quando` vazio = agora (o banco
// usa now()); preenchido vira ISO com o fuso do aparelho.
export function argumentosDaOcorrencia(dados, foto) {
  return {
    p_categoria: dados.categoria,
    p_descricao: String(dados.descricao || '').trim(),
    p_unidade: dados.unidade || null,
    p_foto: foto || null,
    p_ocorrida_em: dados.quando ? new Date(dados.quando).toISOString() : null,
  };
}

// O período da lista de ocorrências e do histórico: os últimos 30 dias do
// hotel. `hoje` é 'AAAA-MM-DD' (hojeDe do contexto).
export function periodoPadrao(hoje) {
  const d = new Date(hoje + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() - 30);
  return { de: d.toISOString().slice(0, 10), ate: hoje };
}

// Até um ano, igual ao banco (historico_de_ocupacao). Erro em texto ou null.
export function validarPeriodo(de, ate) {
  if (!de || !ate) return 'Informe o período.';
  if (ate < de) return 'A data final vem antes da inicial.';
  const dias = (new Date(ate + 'T12:00:00Z') - new Date(de + 'T12:00:00Z')) / 86400000;
  if (dias > 366) return 'O período vai até um ano.';
  return null;
}

// Quem registra e encerra: `editar seguranca` (0057). (A guarda do login de
// proprietário saiu com ele na 0101: o dono não tem acesso ao sistema.)
export function podeRegistrar(contexto) {
  if (!contexto) return false;
  const m = (contexto.modulos || []).find(x => x.chave === 'seguranca');
  return !!(m && m.nivel === 'editar');
}
